import {useState, useCallback} from 'react'

/**
 * Custom hook for managing a filter that can contain multiple values.
 *
 * @template T - The type of values in the filter (string or number).
 * @param {UseFilterOptions<T>} [options] - Optional configuration options for the filter.
 * @param {T[]} [options.initialValues] - Optional array of values initially added to the filter.
 * @param {'single' | 'multiple'} [options.mode] - Optional mode for the filter ('single' or 'multiple').
 * @returns {UseFilterReturnType<T>} An object with properties and functions for managing the filter.
 */

type UseFilterReturnType<T extends string | number> = {
  values: T[]
  addValue: (value: T) => void
  removeValue: (value: T) => void
  setValues: (values: T[]) => void
  toggleValue: (value: T) => void
  clearValues: () => void
  resetValues: () => void
}

interface UseFilterOptions<T> {
  initialValues?: T[]
  mode?: 'single' | 'multiple'
}

export function useFilter<T extends string | number>(
  options?: UseFilterOptions<T>
): UseFilterReturnType<T> {
  const {initialValues = [], mode = 'multiple'} = options ?? {}
  if (initialValues.length > 1 && mode === 'single') {
    throw new Error('Incorrect initial values for single mode')
  }
  const [values, setValues] = useState<T[]>(initialValues)

  const addValue = useCallback(
    (value: T) => {
      setValues((prevValues) => {
        if (mode === 'single') {
          return [value] // Only allow one value at a time
        }
        return Array.from(new Set([...prevValues, value]))
      })
    },
    [mode]
  )

  const removeValue = useCallback((value: T) => {
    setValues((prevValues) => prevValues.filter((v) => v !== value))
  }, [])

  const toggleValue = useCallback(
    (value: T) => {
      setValues((prevValues) => {
        if (mode === 'multiple') {
          if (prevValues.includes(value)) {
            return prevValues.filter((v) => v !== value)
          }
          return [...prevValues, value]
        }
        // mode === 'single'
        if (prevValues.includes(value)) {
          return []
        }
        return [value]
      })
    },
    [mode]
  )

  const clearValues = useCallback(() => {
    setValues([])
  }, [])

  const resetValues = useCallback(() => {
    setValues(initialValues)
  }, [initialValues])

  return {values, addValue, removeValue, setValues, toggleValue, clearValues, resetValues}
}
